import type {
  EntityScope,
  DependentField,
  DependentsResponse,
  FormulaTarget,
  FormulaCatalogResponse,
  FormulaEvaluationResponse,
  FormulaValidationResponse,
} from './formulaFields.interface';
import { http } from '@/lib/ats-https';

// Every formula endpoint refuses with this when the account's flag is off.
export const FORMULA_FIELDS_DISABLED = 'err_formula_fields_disabled';

// The flag has no endpoint of its own: a 403 from the catalog is how the
// frontend learns the whole formula UI should stay hidden for this account.
export const isFormulaFieldsDisabled = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  const codes = JSON.stringify(
    (error as { errors?: unknown })?.errors ?? error ?? ''
  );
  return status === 403 || codes.includes(FORMULA_FIELDS_DISABLED);
};

// The ats-https interceptor unwraps some responses and not others, so accept
// both shapes. `data: null` means the body is empty, not that it is the body.
const body = <T>(response: unknown): T => {
  const wrapper = response as { data?: T } | null;
  if (wrapper && 'data' in wrapper) return wrapper.data as T;
  return response as T;
};

// The drawer holds a multi select as the FE-only kind `rel_tag_multi`, which
// only becomes `rel_tag` + `multi: true` on save. Sent raw, the server has no
// such kind, so it worked out no expected type and refused the formula as a
// field type that can't be calculated — on a target it supports (Gokul,
// CORE-5651). Say it the way the save will.
const targetKind = (kind?: string) =>
  kind === 'rel_tag_multi'
    ? { kind: 'rel_tag', multi: true }
    : { kind, multi: undefined };

export const formulaFieldsApi = {
  // Fields, functions and operators a formula may use, for the pickers.
  getCatalog: async (subjectType: string): Promise<FormulaCatalogResponse> => {
    const response = await http.get('/ats_custom_fields/formula_catalog', {
      params: { subject_type: subjectType },
    });
    return body<FormulaCatalogResponse>(response);
  },

  // The fields this one may reference — narrower than the catalog, since a
  // field can't read itself or anything that would close a cycle.
  getReferenceableFields: async (
    subjectType: string,
    fieldId?: string | number
  ): Promise<FormulaCatalogResponse> => {
    const response = await http.get('/ats_custom_fields/referenceable_fields', {
      params: { subject_type: subjectType, field_id: fieldId },
    });
    return body<FormulaCatalogResponse>(response);
  },

  // Answers as the save does, so the editor cannot show green where it refuses.
  // `kind` names the target while creating; `field_id` once it exists.
  validateFormula: async (
    subjectType: string,
    formula: string,
    target: FormulaTarget = {}
  ): Promise<FormulaValidationResponse> => {
    const response = await http.post('/ats_custom_fields/validate_formula', {
      subject_type: subjectType,
      formula,
      ...targetKind(target.kind),
      field_id: target.fieldId,
      // Sent only when the caller has a scope in hand: omitting them asks the
      // server to read the saved one instead, which is right for the Test
      // panel and wrong for a field being created.
      ...(target.officeIds && { entity_scope_office_ids: target.officeIds }),
      ...(target.departmentIds && {
        entity_scope_department_ids: target.departmentIds,
      }),
    });
    return body<FormulaValidationResponse>(response);
  },

  // Runs the formula against sample values for the drawer's Test button.
  // `inputs` is keyed by each referenced field's handle, not its token.
  evaluateFormula: async (
    subjectType: string,
    formula: string,
    inputs: Record<string, unknown>,
    target: FormulaTarget = {}
  ): Promise<FormulaEvaluationResponse> => {
    const response = await http.post('/ats_custom_fields/evaluate_formula', {
      subject_type: subjectType,
      formula,
      inputs,
      ...targetKind(target.kind),
      field_id: target.fieldId,
      req_id: target.reqId,
      ats_application_id: target.applicationId,
    });
    return body<FormulaEvaluationResponse>(response);
  },

  getDependents: async (
    subjectType: string,
    target: { fieldId?: string | number; token?: string },
    // Proposed, not saved. Sent only by the caller asking what narrowing would
    // break — the hide and delete paths send none, so their request is exactly
    // what it was before and every reader comes back without `in_scope`.
    scope: EntityScope = {}
  ): Promise<DependentField[]> => {
    const response = await http.get('/ats_custom_fields/dependents', {
      params: {
        subject_type: subjectType,
        ...(target.token
          ? { token: target.token }
          : { field_id: target.fieldId }),
        ...(scope.officeIds && { entity_scope_office_ids: scope.officeIds }),
        ...(scope.departmentIds && {
          entity_scope_department_ids: scope.departmentIds,
        }),
      },
    });
    return body<DependentsResponse | null>(response)?.dependents ?? [];
  },
};
